/* eslint-disable no-use-before-define */
/* eslint-disable */

import { delay } from 'redux-saga';
import { all, call, put, take, takeLatest, race, select } from 'redux-saga/effects';
import * as API from '../api/searchFieldsAPI';
import * as actions from '../actions/SearchFieldActions';
import {
  appendEvaluation,
  evaluateSuccess,
  clearEvaluation,
  updateModelInstance
} from '../actions/EvaluationActions';
import { updateGridScrollPosition } from '../actions/UIActions';
import {
  searchImagesQuerySelector,
  searchImagesNextPageQuerySelector
} from '../selectors/SearchFieldSelectors';
import {
  evaluationParameterSelector
} from '../selectors/EvaluationSelector';

function TimeoutException() {
  this.status = 408;
  this.message = 'Request Timeout';
}

function CancelException() {
  this.status = 400;
  this.message = 'Cancelled Request';
}

const timeoutTime = 1200 * 1000;

function requestGeneratorConstructor({
  api,
  parameter,
  payloadAsParameter,
  selector,
  dataProcessCallback,
  before = function beforeHook() {},
  updateAction
}) {
  return function* generatorProcess({ payload }) {
    try {
      const store = yield select();
      yield before(store);

      let apiParams;
      if (payloadAsParameter) {
        apiParams = payload;
      } else if (selector) {
        apiParams = selector(store);
      } else if (dataProcessCallback) {
        apiParams = dataProcessCallback({ store, payload });
      } else {
        apiParams = parameter;
      }

      const { response, timeout, cancel } = yield race({
        response: call(api, apiParams),
        timeout: call(delay, timeoutTime),
        cancel: take('SEARCH_CANCEL')
      });

      if (timeout) {
        throw new TimeoutException();
      } else if (cancel) {
        throw new CancelException();
      }

      yield put(updateAction(response));
      yield put(actions.searchComplete());
    } catch (error) {
      if (error.status === 408) {
        yield put(actions.searchTimeout());
      } else if (error.status === 400) {
        /* DO NOTHING SINCE ACTION ALREADY BEING DISPATCHED BY USER */
        // Uncomment if need to have other way dispatch cancel action
        // yield put(actions.searchCancel());
      } else {
        yield put(actions.searchFailure());
      }
    }
  };
}

export function* searchImageTransaction() {
  yield takeLatest('SEARCH_IMAGE', doFetchImageDataTransaction);
  yield takeLatest('SEARCH_IMAGE', doUpdateCurrentImageNode);
}

function* doFetchImageDataTransaction({ payload }) {
  yield put(actions.updateImageInstances({ instances: [] }));
  yield all([
    call(doFetchImageData, { payload }),
    call(doFetchImageInstances, { payload })
  ]);
}

const doFetchImageData = requestGeneratorConstructor({
  api: API.fetchImage,
  payloadAsParameter: true,
  updateAction: actions.updateImageSearchResult
});

const doFetchImageInstances = requestGeneratorConstructor({
  api: API.fetchInstances,
  payloadAsParameter: true,
  updateAction: actions.updateImageInstances
});

const doUpdateCurrentImageNode = function* a({ payload: imageID }) {
  yield put(actions.clearCurrentImageNode());

  let store;
  /* Keep finding until get the node index of the image */
  do {
    yield put(actions.updateCurrentImageNode(Number(imageID)));
    yield call(delay, 500);
    store = yield select();
  } while (!store.SearchField.currentImageNode);
};

export function* viewImageTransaction() {
  yield takeLatest('VIEW_NEXT_IMAGE', doFetchNextImage);
  yield takeLatest('VIEW_PREVIOUS_IMAGE', doFetchPreviousImage);
}

function* doFetchNextImage() {
  const { SearchField: { currentImageNode } } = yield select();
  const nextImage = currentImageNode.next.value;
  yield put(actions.searchImage(nextImage.sn));
}

function* doFetchPreviousImage() {
  const { SearchField: { currentImageNode } } = yield select();
  const previousImage = currentImageNode.previous.value;
  yield put(actions.searchImage(previousImage.sn));
}

export function* searchImagesTransaction() {
  yield takeLatest('SEARCH_IMAGES', doFetchImagesData);
}

const doFetchImagesData = requestGeneratorConstructor({
  api: API.fetchImages,
  selector: searchImagesQuerySelector,
  before: function* beforeHook(store) {
    const { UI: { scrollContainer } } = store;
    yield put(updateGridScrollPosition(0));
    scrollContainer.scrollTop = 0;
  },
  updateAction: actions.updateImagesSearchResult
});

export function* searchImagesNextPageTransaction() {
  yield takeLatest('SEARCH_IMAGES_NEXT_PAGE', doFetchImagesNextPage);
}

const doFetchImagesNextPage = requestGeneratorConstructor({
  api: API.fetchImages,
  selector: searchImagesNextPageQuerySelector,
  updateAction: actions.updateImagesNextPage
});

export function* autoSearchTransaction() {
  yield all([
    takeLatest('UPDATE_SOURCE_INPUT', doAutoSearch),
    takeLatest('UPDATE_CATEGORY_INPUT', doAutoSearch),
    takeLatest('UPDATE_CAMERA_INPUT', doAutoSearch),
    takeLatest('UPDATE_CAMERA_ID_INPUT', doAutoSearch),
    takeLatest('UPDATE_BATCH_INPUT', doAutoSearch),
    takeLatest('UPDATE_IMAGE_ID_INPUT', doAutoSearch),
    takeLatest('UPDATE_IMAGE_SIZE', doAutoSearch),
    takeLatest('UPDATE_IMAGE_SN_INPUT', doAutoSearch),
    takeLatest('UPDATE_EVALUATED_IMAGE_FILTER', doAutoSearch),
    takeLatest('DISABLE_IMAGE_SIZE_SEARCH', doAutoSearch)
  ]);
}

function* doAutoSearch() {
  const autoSearch = yield select(state => state.SearchField.autoSearch);
  if (autoSearch) {
    yield put(actions.searchImages());
  }
}

export function* fetchEvaluationInstanceTransaction() {
  yield takeLatest('EVALUATE_MODEL_INSTANCE', doFetchEvaluationInstance);
}

const doFetchEvaluationInstance = requestGeneratorConstructor({
  api: API.fetchEvaluatedInstance,
  payloadAsParameter: true,
  updateAction: updateModelInstance
});


export function* fetchEvaluationTransaction() {
  yield takeLatest('EVALUATE_START', doFetchEvaluationData);
}

function* doFetchEvaluationData({ payload }) {
  yield put(clearEvaluation());

  console.log('start fetching');

  try {
    const store = yield select();

    const params = evaluationParameterSelector(store);

    const { models } = params;
    delete params.models;

    for (let i = 0; i < models.length; i += 1) {
      const sn = models[i];
      yield call(doEvaluateModel, { ...params, model_sn: sn });
    }
    yield put(evaluateSuccess());
  } catch (error) {
    if (error.status === 408) {
      yield put(actions.searchTimeout());
    } else if (error.status === 400) {
      /* DO NOTHING SINCE ACTION ALREADY BEING DISPATCHED BY USER */
      // Uncomment if need to have other way dispatch cancel action
      // yield put(actions.searchCancel());
    } else {
      yield put(actions.searchFailure());
    }
  }
}

function* doEvaluateModel(apiParams) {
  const api = API.fetchMetrics;

  const { response, timeout, cancel } = yield race({
    response: call(api, apiParams),
    timeout: call(delay, timeoutTime),
    cancel: take('SEARCH_CANCEL')
  });

  if (timeout) {
    throw new TimeoutException();
  } else if (cancel) {
    throw new CancelException();
  }

  const store = yield select();
  const { modelSnMap } = store.SearchParameter;
  const { model_sn: sn } = apiParams;

  yield put(appendEvaluation({
    response,
    model: { sn, name: modelSnMap.get(sn) },
  }));
}
//F5rjTYBHwpf4eCt5



// WEBPACK FOOTER //
// ./src/sagas/SearchFieldSaga.js